import type { Facet } from '../experiments/library-learnings';

/**
 * The Library's element and theme filters, as pure functions over the rows.
 *
 * Both filters take values the model wrote: element names and theme names are model text. A
 * `?element=` or `?theme=` URL value is therefore accepted only when it is in the facet set
 * built from the rows actually loaded (acceptFacetValue), exactly as `?mechanism=` is narrowed
 * to the known mechanism ids. A crafted URL, or a name that has since changed, becomes no filter
 * rather than a filter that matches nothing, or something that is rendered back as text.
 *
 * Typed on the minimum a row must carry, so these work on a LibraryRow once its insight variants
 * carry `themeId`, without importing the row type.
 */
type InsightVariant = { elementName: string; themeId?: number | null };
type FilterableRow = { insight?: { variants: readonly InsightVariant[] } | null };

export type LiveTheme = { id: number; name: string };

type ThemedVariant = InsightVariant & { themeName?: string | null };
type ThemedRow = { insight?: { variants: readonly ThemedVariant[] } | null };

/** The live themes the loaded rows carry, de-duplicated by id. A retired theme arrives as null and is skipped. */
export function liveThemesOf(rows: readonly ThemedRow[]): LiveTheme[] {
  const themes = new Map<number, string>();
  for (const row of rows) {
    for (const variant of row.insight?.variants ?? []) {
      if (variant.themeId != null && variant.themeName) themes.set(variant.themeId, variant.themeName);
    }
  }
  return [...themes.entries()].map(([id, name]) => ({ id, name }));
}

export type ElementThemeFilters = { element: string; theme: string };

export const hasElement = (row: FilterableRow, element: string): boolean =>
  !!row.insight?.variants.some((variant) => variant.elementName === element);

export const hasTheme = (row: FilterableRow, theme: string): boolean =>
  !!row.insight?.variants.some((variant) => variant.themeId != null && String(variant.themeId) === theme);

/** Whether a row passes both filters. A row with no card never matches either: it has no labels to match. */
export function matchesElementTheme(row: FilterableRow, { element, theme }: ElementThemeFilters): boolean {
  if (element && !hasElement(row, element)) return false;
  if (theme && !hasTheme(row, theme)) return false;
  return true;
}

const byCountThenLabel = <K extends string>(a: Facet<K>, b: Facet<K>): number =>
  b.count - a.count || a.label.localeCompare(b.label);

/** Every element name that occurs, most used first. Only names that occur, so the filter never offers an empty result. */
export function elementFacets(rows: readonly FilterableRow[]): Facet<string>[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const names = new Set(row.insight?.variants.map((variant) => variant.elementName) ?? []);
    for (const name of names) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()].map(([name, count]) => ({ value: name, label: name, count })).sort(byCountThenLabel);
}

/** The website's live themes that at least one row is in, most used first. The value is the id, as it travels in the URL. */
export function themeFacets(rows: readonly FilterableRow[], themes: readonly LiveTheme[]): Facet<string>[] {
  return themes
    .map((theme) => ({
      value: String(theme.id),
      label: theme.name,
      count: rows.filter((row) => hasTheme(row, String(theme.id))).length,
    }))
    .filter((facet) => facet.count > 0)
    .sort(byCountThenLabel);
}

/** The URL value if it is one of the facets, else no filter. */
export const acceptFacetValue = (param: string | null | undefined, facets: readonly Facet<string>[]): string =>
  param && facets.some((facet) => facet.value === param) ? param : '';
